import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { RECONCILIATION_NS as NS } from './constants';
import { runOutcome } from './helpers';
import type { RunReconciliationForm } from './useRunReconciliation';

const COLOR = {
  QUEUED: 'default',
  RUNNING: 'info',
  DEFERRED: 'warning',
  DONE: 'success',
  FAILED: 'error',
} as const;

/**
 * XBIT-4864: "Run reconciliation now" and the run's outcome. The run needs one operator and clears no freeze itself: only a
 * RECONCILED run after a two-person resolution does, in the engine. DEFERRED means the daily cut-off still holds the vault.
 */
export const RunDialog = ({
  form,
  vaultName,
}: {
  form: RunReconciliationForm;
  vaultName: string;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const t = (key: string, options?: object) => translate(`${NS}.run.${key}`, options);
  const { run } = form;
  const outcome = run ? runOutcome(run) : null;
  const waiting = form.polling;
  const stalled = run !== null && !outcome?.terminal && !form.polling;

  return (
    <Dialog
      open={form.isOpen}
      onClose={form.submitting ? undefined : form.close}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{t('title', { name: vaultName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">{t('body')}</Typography>
          {form.alert && (
            <Alert severity={form.alert.severity}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {form.alert.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
          {run && (
            <Stack spacing={1} aria-live="polite">
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Chip size="small" color={COLOR[run.status]} label={t(`status.${run.status}`)} />
                {waiting && <CircularProgress size={16} />}
              </Stack>
              <Typography variant="body2">{t(`explain.${run.status}`)}</Typography>
              {outcome?.detail && (
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
                >
                  {run.status === 'DONE' ? t(`verdict.${outcome.detail}`) : outcome.detail}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.close} disabled={form.submitting}>
          {run ? t('close') : t('back')}
        </Button>
        {stalled && <Button onClick={form.resume}>{t('checkAgain')}</Button>}
        {!run && (
          <LoadingButton
            variant="contained"
            onClick={form.submit}
            disabled={form.submitting}
            loading={form.submitting}
          >
            {form.alert ? t('retry') : t('confirm')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
